import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fmtDateTime } from "@/lib/api";
import { PageTitle, EmptyState, Field, StatusBadge } from "@/components/vaulted";

const empty = { name: "", description: "", permissions: [] };

function PermissionGrid({ areas, value, onChange }) {
  const has = (p) => value.includes(p);
  const toggle = (area, level, on) => {
    const view = `${area}.view`, manage = `${area}.manage`;
    let next = value.filter((p) => p !== view && p !== manage);
    if (on) next = level === "manage" ? [...next, view, manage] : [...next, view];
    else if (level === "view") next = next.filter((p) => p !== manage);
    else if (has(view)) next = [...next, view];
    onChange(next);
  };
  return (
    <div className="overflow-hidden rounded-md border border-[#27272A]" data-testid="permission-grid">
      <div className="grid grid-cols-[1fr_70px_70px] bg-[#18181B] px-3 py-2 text-[10px] uppercase tracking-wider text-zinc-500"><span>Area</span><span className="text-center">View</span><span className="text-center">Manage</span></div>
      {areas.map((a) => (
        <div key={a.area} className="grid grid-cols-[1fr_70px_70px] items-center border-t border-[#27272A] px-3 py-1.5 text-sm">
          <span className="text-zinc-300">{a.label}</span>
          {["view", "manage"].map((lvl) => (
            <input key={lvl} type="checkbox" data-testid={`perm-${a.area}-${lvl}`} checked={has(`${a.area}.${lvl}`)}
              onChange={(e) => toggle(a.area, lvl, e.target.checked)} className="mx-auto accent-violet-500" />
          ))}
        </div>
      ))}
    </div>
  );
}

function RoleDialog({ role, areas, onClose, onSaved }) {
  const [f, setF] = useState(role?.id ? { name: role.name, description: role.description, permissions: role.permissions } : empty);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      if (role?.id) await api.put(`/admin/staff-roles/${role.id}`, f); else await api.post("/admin/staff-roles", f);
      toast.success("Role saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-xl bg-[#111116] border-[#27272A] max-h-[88vh] overflow-y-auto" data-testid="staff-role-dialog">
      <DialogHeader><DialogTitle>{role?.id ? "Edit role" : "New staff role"}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        <Field label="Name"><input data-testid="role-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="v-input" placeholder="e.g. Moderator" /></Field>
        <Field label="Description"><input data-testid="role-description-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="v-input" /></Field>
        <PermissionGrid areas={areas} value={f.permissions} onChange={(permissions) => setF({ ...f, permissions })} />
        <p className="text-[11px] text-zinc-500">Manage includes view. Admins and the owner always have full access.</p>
        <button data-testid="role-save-button" disabled={busy || f.name.trim().length < 2} onClick={save} className="v-btn-primary w-full">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Save role</button>
      </div>
    </DialogContent></Dialog>
  );
}

export function AdminStaffRoles() {
  const [roles, setRoles] = useState(null);
  const [areas, setAreas] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/staff-roles").then((r) => setRoles(r.data)).catch((e) => { setRoles([]); toast.error(errMsg(e)); });
  useEffect(() => { load(); api.get("/admin/permissions").then((r) => setAreas(r.data)); }, []);
  const remove = (r) => window.confirm(`Delete "${r.name}"? ${r.member_count} member(s) will lose their staff access.`) &&
    api.delete(`/admin/staff-roles/${r.id}`).then(() => { toast.success("Role deleted"); load(); }).catch((e) => toast.error(errMsg(e)));
  const label = (p) => { const [a, l] = p.split("."); return `${areas.find((x) => x.area === a)?.label || a}${l === "manage" ? " · manage" : ""}`; };
  return (
    <div data-testid="admin-staff-roles">
      <PageTitle title="Staff Roles" subtitle="Control which Staff Panel areas each team member can see and change. Assign roles in Staff Accounts."
        action={<button data-testid="create-role-button" onClick={() => setEdit({})} className="v-btn-primary v-btn-sm"><Plus className="h-4 w-4" />New role</button>} />
      {!roles ? <Loader2 className="h-5 w-5 animate-spin text-violet-400" /> : !roles.length ? <EmptyState icon={ShieldCheck} title="No staff roles" testId="staff-roles-empty" /> :
        <div className="grid gap-3 md:grid-cols-2">{roles.map((r) => (
          <div key={r.id} className="v-card p-5" data-testid={`staff-role-${r.name_lower}`}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1"><p className="font-medium text-white">{r.name}</p><p className="text-xs text-zinc-500">{r.description || "—"} · {r.member_count} member{r.member_count === 1 ? "" : "s"}</p></div>
              <button data-testid={`edit-role-${r.name_lower}`} onClick={() => setEdit(r)} className="text-zinc-500 hover:text-white"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`delete-role-${r.name_lower}`} onClick={() => remove(r)} className="text-zinc-500 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">{r.permissions.filter((p) => !(p.endsWith(".view") && r.permissions.includes(p.replace(".view", ".manage")))).map((p) => (
              <span key={p} className={`rounded border px-1.5 py-0.5 text-[10px] ${p.endsWith(".manage") ? "border-violet-500/40 text-violet-200" : "border-[#27272A] text-zinc-400"}`}>{label(p)}</span>))}</div>
          </div>))}</div>}
      {edit && <RoleDialog role={edit} areas={areas} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

const LEVELS = [["staff", "Staff (custom role)"], ["admin", "Admin (full access)"]];

function StaffAccountDialog({ acct, roles, isOwner, onClose, onSaved }) {
  const isNew = !acct.id;
  const [f, setF] = useState({ username: acct.username || "", password: "", level: acct.roles?.includes("ADMIN") ? "admin" : "staff", staff_role_id: acct.staff_role_id || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    const role = f.level === "staff" ? f.staff_role_id || null : null;
    try {
      if (isNew) await api.post("/admin/staff-accounts", { username: f.username, password: f.password, level: f.level, staff_role_id: role });
      else await api.patch(`/admin/staff-accounts/${acct.id}`, { ...(f.password ? { password: f.password } : {}), ...(isOwner ? { level: f.level } : {}), staff_role_id: role });
      toast.success(isNew ? "Staff account created" : "Staff account updated"); onSaved();
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}><DialogContent className="max-w-md bg-[#111116] border-[#27272A]" data-testid="staff-account-dialog">
      <DialogHeader><DialogTitle>{isNew ? "New staff account" : `Edit ${acct.username}`}</DialogTitle></DialogHeader>
      <div className="space-y-4">
        {isNew && <Field label="Username" hint="3–24 letters, numbers, . _ -"><input data-testid="staff-username-input" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="v-input" autoComplete="off" /></Field>}
        <Field label={isNew ? "Password" : "New password"} hint={isNew ? "At least 8 characters" : "Leave blank to keep the current password. Changing it logs them out."}>
          <input data-testid="staff-password-input" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="v-input" autoComplete="new-password" /></Field>
        {isOwner && <Field label="Access level"><select data-testid="staff-level-select" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })} className="v-input">
          {LEVELS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>}
        {f.level === "staff" && <Field label="Staff role"><select data-testid="staff-role-select" value={f.staff_role_id} onChange={(e) => setF({ ...f, staff_role_id: e.target.value })} className="v-input">
          <option value="">No role (no access)</option>{roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}</select></Field>}
        <button data-testid="staff-account-save" disabled={busy || (isNew && (f.username.length < 3 || f.password.length < 8)) || (!!f.password && f.password.length < 8)} onClick={save} className="v-btn-primary w-full">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />}{isNew ? "Create account" : "Save changes"}</button>
      </div>
    </DialogContent></Dialog>
  );
}

export function AdminStaffAccounts() {
  const { user } = useAuth();
  const isOwner = !!user?.roles?.includes("OWNER");
  const [list, setList] = useState(null);
  const [roles, setRoles] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = () => api.get("/admin/staff-accounts").then((r) => setList(r.data)).catch((e) => { setList([]); toast.error(errMsg(e)); });
  useEffect(() => { load(); api.get("/admin/staff-roles").then((r) => setRoles(r.data)); }, []);
  const roleName = (a) => a.roles.includes("OWNER") ? "Owner" : a.roles.includes("ADMIN") ? "Admin" : roles.find((r) => r.id === a.staff_role_id)?.name || "No role";
  const locked = (a) => a.id === user?.id || a.roles.includes("OWNER") || (a.roles.includes("ADMIN") && !isOwner);
  const act = (a, body, msg) => api.patch(`/admin/staff-accounts/${a.id}`, body).then(() => { toast.success(msg); load(); }).catch((e) => toast.error(errMsg(e)));
  const remove = (a) => window.confirm(`Delete staff account "${a.username}"? They will be logged out immediately.`) &&
    api.delete(`/admin/staff-accounts/${a.id}`).then(() => { toast.success("Staff account deleted"); load(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div data-testid="admin-staff-accounts">
      <PageTitle title="Staff Accounts" subtitle="Create a username and password for each team member. They sign in at /staff/login."
        action={<button data-testid="create-staff-account-button" onClick={() => setEdit({})} className="v-btn-primary v-btn-sm"><Plus className="h-4 w-4" />New staff account</button>} />
      {!list ? <Loader2 className="h-5 w-5 animate-spin text-violet-400" /> :
        <div className="v-panel overflow-x-auto"><table className="v-table w-full" style={{ minWidth: 760 }}>
          <thead><tr>{["Username", "Access", "Status", "Last login", "Created by", ""].map((h) => <th key={h}>{h}</th>)}</tr></thead>
          <tbody>{list.map((a) => (
            <tr key={a.id} data-testid={`staff-account-row-${a.username.toLowerCase()}`}>
              <td className="font-medium text-white">{a.username}{a.id === user?.id && <span className="ml-2 text-[10px] text-violet-300">you</span>}</td>
              <td>{roleName(a)}</td>
              <td><StatusBadge status={a.status} /></td>
              <td className="text-zinc-500">{a.last_login_at ? fmtDateTime(a.last_login_at) : "Never"}</td>
              <td className="text-zinc-500">{a.created_by}</td>
              <td className="whitespace-nowrap text-right">{!locked(a) && <span className="inline-flex gap-1.5">
                <button data-testid={`edit-staff-${a.username.toLowerCase()}`} onClick={() => setEdit(a)} className="v-btn-ghost v-btn-sm"><Pencil className="h-3.5 w-3.5" /></button>
                <button data-testid={`toggle-staff-${a.username.toLowerCase()}`} onClick={() => act(a, { status: a.status === "suspended" ? "active" : "suspended" }, a.status === "suspended" ? "Reactivated" : "Suspended")}
                  className="v-btn-ghost v-btn-sm">{a.status === "suspended" ? "Reactivate" : "Suspend"}</button>
                <button data-testid={`delete-staff-${a.username.toLowerCase()}`} onClick={() => remove(a)} className="v-btn-ghost v-btn-sm text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
              </span>}</td>
            </tr>))}</tbody>
        </table></div>}
      {edit && <StaffAccountDialog acct={edit} roles={roles} isOwner={isOwner} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}

export function StaffLoginPage() {
  const { user, staffLogin } = useAuth();
  const nav = useNavigate();
  const [f, setF] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (user && user.account_type === "staff") return <Navigate to="/admin" replace />;
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try { await staffLogin(f.username.trim(), f.password); nav("/admin"); } catch (ex) { setErr(errMsg(ex)); setBusy(false); }
  };
  return (
    <div className="container-v grid min-h-[70vh] place-items-center py-14">
      <form onSubmit={submit} className="v-panel w-full max-w-sm p-7" data-testid="staff-login-form">
        <ShieldCheck className="h-8 w-8 text-violet-400" />
        <h1 className="mt-4 text-xl font-semibold text-white">Staff login</h1>
        <p className="mt-1 text-sm text-zinc-400">Use the username and password your admin gave you.</p>
        <div className="mt-6 space-y-4">
          <Field label="Username"><input data-testid="staff-login-username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} className="v-input" autoComplete="username" autoFocus /></Field>
          <Field label="Password"><input data-testid="staff-login-password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="v-input" autoComplete="current-password" /></Field>
          {err && <p data-testid="staff-login-error" className="text-sm text-red-400">{err}</p>}
          <button data-testid="staff-login-submit" disabled={busy || !f.username || !f.password} className="v-btn-primary w-full">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Log in</button>
        </div>
      </form>
    </div>
  );
}
